/**
 * A conversa da tela com o servidor.
 *
 * Repare no que NÃO existe aqui: nenhuma senha, nenhum token, nenhum cabeçalho
 * de autorização. O crachá viaja num cookie `HttpOnly`, que o navegador manda
 * sozinho e o JavaScript desta página não consegue ler. Não há o que guardar,
 * o que vazar, nem o que esquecer de limpar.
 *
 * O que fica guardado no aparelho é só um LEMBRETE — nome, perfil e fazenda —
 * para o app abrir mostrando a tela certa quando o tratador liga o celular sem
 * sinal. Lembrete não é credencial: com ele ninguém entra em lugar nenhum, e
 * na primeira vez que houver sinal o servidor confirma ou desmente.
 */

import { enfileirar, novoId, pendentes, type Pendente } from './fila'
import { pedir } from './rede'
import type { Calibragem, Pilha } from '../dominio/pilhas'
import { emSacos, inteiroDoServidor } from '../dominio/unidades'
import type { LinhaGravada } from '../dominio/romaneio'
import type { MovimentoDoLote } from '../dominio/fechamento'

export type Perfil = 'tratador' | 'gestor' | 'administrador'

export interface Eu {
  nome: string
  perfil: Perfil
  fazenda: string
}

export interface Pessoa {
  id: string
  nome: string
  email: string | null
  perfil: Perfil
  ativo: boolean
}

export interface Aparelho {
  sessao_id: string
  pessoa: string
  perfil: Perfil
  descricao_aparelho: string | null
  criada_em: string
  ultimo_uso_em: string | null
  expira_em: string
}

export class ErroDoServidor extends Error {
  constructor(readonly status: number, mensagem: string) {
    super(mensagem)
  }
}

/** Erro de rede, que é coisa diferente de erro de permissão — ver `App.tsx`. */
export class SemSinal extends Error {}

/**
 * A conexão falhou: o pedido saiu e a resposta não voltou direito. É um jeito
 * de estar sem sinal — por isso herda dele, e quem só pergunta «é problema de
 * rede?» continua acertando.
 */
export class ConexaoFalhou extends SemSinal {}

const LEMBRETE = 'cunhatai.eu'

/**
 * LISTA QUE NUNCA É `undefined`.
 *
 * O tipo promete um arranjo, mas quem cumpre a promessa é o servidor — e
 * resposta de rede é afirmada, não verificada. Uma resposta fora do formato
 * esperado virava `undefined.map(...)` e derrubava a tela inteira para branco,
 * sem mensagem, com o tratador em pé na beira do tanque.
 *
 * Tela vazia diz «não há nada aqui», que às vezes é a verdade. Tela branca não
 * diz nada.
 */
function lista<T>(valor: T[] | undefined | null): T[] {
  return Array.isArray(valor) ? valor : []
}

async function chamar<T>(caminho: string, corpo?: unknown): Promise<T> {
  // A MESMA CONVERSA DA FILA (`rede.ts`): com limite de espera, e com o erro
  // passageiro separado da recusa. Antes as telas não tinham nenhum dos dois.
  const d = await pedir(caminho, corpo)
  if (d.tipo === 'ok') return d.dados as T
  if (d.tipo === 'sem_conexao') throw new SemSinal('não há conexão')
  if (d.tipo === 'falhou') throw new ConexaoFalhou('conexão falhou')
  if (d.tipo === 'sem_acesso') throw new ErroDoServidor(401, d.erro)
  throw new ErroDoServidor(d.status, d.erro)
}

// ---------------------------------------------------------------------------
// Entrar e sair
// ---------------------------------------------------------------------------

export async function entrar(codigo: string): Promise<Eu> {
  const eu = await chamar<Eu>('entrar', { codigo })
  guardarLembrete(eu)
  return eu
}

export async function quemSouEu(): Promise<Eu> {
  const eu = await chamar<Eu>('eu')
  guardarLembrete(eu)
  return eu
}

export async function sair(): Promise<void> {
  try {
    await chamar('sair', {})
  } finally {
    esquecerLembrete()
  }
}

export function lembrete(): Eu | null {
  try {
    const bruto = localStorage.getItem(LEMBRETE)
    return bruto ? (JSON.parse(bruto) as Eu) : null
  } catch {
    return null
  }
}

function guardarLembrete(eu: Eu): void {
  try {
    localStorage.setItem(LEMBRETE, JSON.stringify(eu))
  } catch {
    // Navegador com armazenamento bloqueado: o app funciona igual, só abre
    // pedindo confirmação ao servidor em vez de abrir direto.
  }
}

export function esquecerLembrete(): void {
  try {
    localStorage.removeItem(LEMBRETE)
  } catch {
    /* idem */
  }
}

// ---------------------------------------------------------------------------
// Administração de acesso
// ---------------------------------------------------------------------------

export async function listarPessoas(): Promise<Pessoa[]> {
  const { pessoas } = await chamar<{ pessoas: Pessoa[] }>('pessoas')
  return lista(pessoas)
}

/** Sem `id` cria; com `id` corrige. É a mesma rota, como na de tanques. */
export async function salvarPessoa(
  dados: { id?: string; nome: string; email: string; perfil: Perfil },
): Promise<Pessoa> {
  const { pessoa } = await chamar<{ pessoa: Pessoa }>('pessoas', dados)
  return pessoa
}

export async function desligarPessoa(pessoaId: string, ativo: boolean): Promise<void> {
  await chamar('desligar-pessoa', { pessoa_id: pessoaId, ativo })
}

export interface AcessoGerado {
  codigo: string
  expira_em: string
  dias: number
}

export async function gerarAcesso(pessoaId: string): Promise<AcessoGerado> {
  return chamar<AcessoGerado>('acessos', { pessoa_id: pessoaId })
}

// ---------------------------------------------------------------------------
// Tanques
// ---------------------------------------------------------------------------

/** As quatro etiquetas de fase. Quem escolhe é o gestor — ver regra 3. */
export type UsoDeTanque = 'alevinagem' | 'juvenil' | 'engorda' | 'misto'

export interface Tanque {
  id: string
  codigo: string
  nome: string
  area_m2: number
  profundidade_media_m: number | null
  uso_habitual: UsoDeTanque | null
  ativo: boolean
  observacao: string | null
  /** Da migração 005. `represa` é fonte de água; `escavado` é tanque de produção. */
  tipo: 'represa' | 'escavado' | null
  alimentado_por: string | null
  alimentado_por_codigo: string | null
  carga_final_alvo_t_ha: number | null
  ordem_ronda: number | null
  /** O agrupamento da tela. Nulo quando a fazenda não divide em setores. */
  setor: string | null
}

/**
 * Sem `id` o servidor cria; com `id`, corrige. É a mesma rota.
 *
 * Os campos da 005 ficam de fora — menos a carga-alvo, que a tela passou a
 * editar em 23/09/2026. E ela é OPCIONAL de propósito: o servidor só mexe nela
 * quando a chave vem no pedido. O botão de tirar de uso não a manda, e por isso
 * não a apaga — o tropeço que o setor já deu uma vez.
 */
export type TanqueParaSalvar =
  Omit<Tanque, 'id' | 'tipo' | 'alimentado_por' | 'alimentado_por_codigo'
               | 'carga_final_alvo_t_ha' | 'ordem_ronda'>
  & { id?: string; carga_final_alvo_t_ha?: number | null }

export async function listarTanques(): Promise<Tanque[]> {
  const { tanques } = await chamar<{ tanques: Tanque[] }>('tanques')
  return lista(tanques)
}

export async function salvarTanque(tanque: TanqueParaSalvar): Promise<Tanque> {
  const resposta = await chamar<{ tanque: Tanque }>('tanques', tanque)
  return resposta.tanque
}

// ---------------------------------------------------------------------------
// Ciclos
// ---------------------------------------------------------------------------

export type Fase = 'alevinagem' | 'juvenil' | 'engorda' | 'misto'

export interface Ciclo {
  id: string
  codigo: string
  fase: Fase
  data_inicio: string
  observacao: string | null
  tanque_id: string
  tanque: string
  area_m2: number
  tipo: 'represa' | 'escavado' | null
  carga_final_alvo_t_ha: number | null
  encerrado: boolean
  /** Nula enquanto o ciclo corre. Diz QUANDO deixou de ocupar o tanque. */
  data_encerramento: string | null
  /** Do povoamento. `quantidade` é nula quando o peixe entrou pesado. */
  quantidade: number | null
  peso_total_kg: number | null
  peso_medio_g: number | null
  data_povoamento: string | null
  /** A última vez que este peixe subiu numa balança — âncora da estimativa. */
  peso_medido_g: number | null
  data_medida: string | null
  fonte: string | null
  /** Peixes VIVOS hoje: entraram menos o que saiu. Medido. */
  peixes_hoje: number
  /** Toda a ração que este ciclo já recebeu, em kg. Medido. */
  racao_acumulada_kg: number
}

export interface CicloNovo {
  tanque_id: string
  fase: Fase
  data_inicio: string
  modo_apuracao: 'contagem' | 'pesagem'
  quantidade: number | null
  peso_total_kg: number | null
  peso_medio_g: number
  observacao: string | null
}

export async function listarCiclos(): Promise<Ciclo[]> {
  const { ciclos } = await chamar<{ ciclos: Ciclo[] }>('ciclos')
  // `peixes_hoje` vem de `sum(integer)`, que é BIGINT e chega como TEXTO. A
  // consulta já converte com `::int`; isto aqui é a segunda defesa, para a
  // próxima consulta que esquecer. Ver `inteiroDoServidor`.
  //
  // `tratos_hoje` e `faltas_hoje` também vêm nesta resposta e continuam fora
  // do tipo: nenhuma tela os lê daqui.
  return lista(ciclos).map((c) => ({
    ...c,
    peixes_hoje: inteiroDoServidor(c.peixes_hoje),
    racao_acumulada_kg: Number(c.racao_acumulada_kg) || 0,
  }))
}

export async function abrirCiclo(dados: CicloNovo): Promise<{ id: string; codigo: string }> {
  const { ciclo } = await chamar<{ ciclo: { id: string; codigo: string } }>('ciclos', dados)
  return ciclo
}

// ---------------------------------------------------------------------------
// A ronda da manhã
// ---------------------------------------------------------------------------

export interface LeituraDeAgua {
  data: string
  hora: string | null
  temperatura_c: number | null
  oxigenio_mgl: number | null
  ceu: CeuDoDia | null
  /** Está na fila do aparelho e o servidor ainda não sabe dela. */
  pendente?: boolean
  id?: string
}

/**
 * Uma análise de pH e alcalinidade — avulsa, em outra hora que a ronda, e fora
 * de qualquer conta. Lista própria para nunca se misturar com a de oxigênio.
 */
export interface AnaliseDaAgua {
  data: string
  hora: string | null
  ph: number | null
  alcalinidade_mgl: number | null
  pendente?: boolean
  id?: string
}

export type CeuDoDia = 'sol' | 'parcial' | 'nublado' | 'chuva'

export interface PontoDaRonda {
  ciclo_id: string
  ciclo: string
  fase: Fase
  tanque_id: string
  tanque: string
  tanque_nome: string
  area_m2: number
  tipo: 'represa' | 'escavado' | null
  ordem_ronda: number | null
  setor: string | null
  data_povoamento: string | null
  quantidade: number | null
  peso_medio_g: number | null
  /** Oxigênio e temperatura, da mais recente para a mais antiga. */
  leituras: LeituraDeAgua[]
  /** pH e alcalinidade, à parte. Ver `AnaliseDaAgua`. */
  quimica: AnaliseDaAgua[]
  /** A última vez que este peixe subiu numa balança. */
  peso_medido_g: number | null
  data_medida: string | null
  fonte: string | null
  /** Vivos hoje: entrou menos o que saiu. */
  peixes_hoje: number
  tratado_hoje_kg: number
  tratos_hoje: number
  /** Os tratos do dia, em ordem de hora — não só quantos foram. */
  tratos_do_dia: TratoDoDia[]
  faltas_hoje: number
}

/**
 * Um trato já registrado no servidor, para a tela poder listá-lo.
 *
 * Existe porque contar não bastou: em 21/09/2026 um tanque recebeu três
 * tratos e quem olhava a tela sabia de um. Número responde «quantos»; quem
 * está no tanque precisa de «quais, e a que horas».
 */
export interface TratoDoDia {
  id: string
  hora: string | null
  sequencia: number
  quantidade_kg: number
  unidade_informada: 'saco' | 'kg'
  quantidade_informada: number
}

export interface MotivoDeFalta {
  codigo: string
  descricao: string
  classificacao: 'justificada' | 'operacional' | 'critica' | 'nao_classificada'
}

export interface TratoNovo {
  ciclo_id: string
  data: string
  hora: string | null
  unidade_informada: 'saco' | 'kg'
  quantidade_informada: number
  granulometria_min_mm: number
  granulometria_max_mm: number
  proteina_pct: number
  sugerido_kg: number | null
  coeficiente_aplicado: number | null
  limitou: string | null
}

/**
 * GRAVA NO APARELHO, não no servidor.
 *
 * As três funções de registro — trato, falta e leitura — só entram na fila.
 * Elas não falham por falta de sinal porque não tocam a rede: quem toca é o
 * botão Sincronizar, e só quando o tratador aperta.
 *
 * É a regra do `CLAUDE.md` virando código: «dados gravam primeiro no aparelho
 * e sincronizam depois — sempre, sem exceção».
 */
export function registrarTrato(dados: TratoNovo, onde: Etiqueta = {}): Pendente {
  // O resumo diz as DUAS unidades quando o lançamento é em saco. Quem confere
  // a fila está conferindo contra o galpão, onde se conta saco, e contra o
  // relatório, onde se soma quilo — e a conversão de cabeça é onde se erra.
  const q = dados.quantidade_informada
  const saco = onde.pesoSaco ?? 25
  const resumo =
    dados.unidade_informada === 'saco'
      ? `${emSacos(q)} ${q === 1 ? 'saco' : 'sacos'} · ${(q * saco).toLocaleString('pt-BR')} kg`
      : `${q.toLocaleString('pt-BR')} kg`
  return enfileirar(
    'trato',
    { ...dados },
    { dia: dados.data, ciclo_id: dados.ciclo_id, rotulo: onde.rotulo ?? null, resumo },
  )
}

/** O nome do tanque, para a fila conseguir se mostrar sem pedir nada ao servidor. */
export interface Etiqueta {
  rotulo?: string | null
  pesoSaco?: number
}

export async function listarMotivos(): Promise<MotivoDeFalta[]> {
  const { motivos } = await chamar<{ motivos: MotivoDeFalta[] }>('motivos-falta')
  return lista(motivos)
}

/**
 * A falta geral marca vários ciclos de uma vez, e por isso entra na fila como
 * um registro por ciclo: se metade subir e metade não, o reenvio precisa saber
 * qual metade falta. Um pedido só com N identificadores não daria isso.
 */
export function registrarFalta(
  ciclos: string[],
  data: string,
  motivoCodigo: string,
  observacao: string | null,
  rotulos: Record<string, string> = {},
): number {
  for (const ciclo of ciclos) {
    const id = novoId()
    enfileirar(
      'falta',
      { id, ids: [id], ciclos: [ciclo], data, motivo_codigo: motivoCodigo, observacao },
      { dia: data, ciclo_id: ciclo, rotulo: rotulos[ciclo] ?? null, resumo: 'não tratou' },
    )
  }
  return ciclos.length
}

export interface LeituraNova {
  tanque_id: string
  data: string
  hora: string | null
  temperatura_c: number | null
  oxigenio_mgl: number | null
  /** Análise química: pode vir sozinha, sem oxigênio nem temperatura. */
  ph: number | null
  alcalinidade_mgl: number | null
  ceu: CeuDoDia | null
  observacao: string | null
}

export async function listarRonda(dia?: string): Promise<PontoDaRonda[]> {
  const { ronda } = await chamar<{ ronda: PontoDaRonda[] }>(
    dia ? `ronda?dia=${encodeURIComponent(dia)}` : 'ronda',
  )
  // `tratos_do_dia` pode faltar enquanto um celular estiver numa versão velha
  // do app falando com o servidor novo, ou o contrário. Lista ausente vira
  // lista vazia aqui, num lugar só, em vez de virar `undefined.map` na tela.
  return lista(ronda).map((p) => ({
    ...p,
    tratos_do_dia: lista(p.tratos_do_dia),
    quimica: lista(p.quimica),
    // OS TRÊS QUE VÊM DE count() E sum(): bigint, que o driver entrega como
    // texto. Ver `inteiroDoServidor`. A consulta já converte com `::int`; isto
    // aqui é a segunda defesa, para a próxima consulta que esquecer.
    peixes_hoje: inteiroDoServidor(p.peixes_hoje),
    tratos_hoje: inteiroDoServidor(p.tratos_hoje),
    faltas_hoje: inteiroDoServidor(p.faltas_hoje),
  }))
}

export function registrarLeitura(dados: LeituraNova, onde: Etiqueta = {}): LeituraDeAgua {
  const partes = [
    dados.oxigenio_mgl === null ? null : `${dados.oxigenio_mgl.toLocaleString('pt-BR')} mg/L`,
    dados.temperatura_c === null ? null : `${dados.temperatura_c.toLocaleString('pt-BR')} °C`,
    dados.ph === null ? null : `pH ${dados.ph.toLocaleString('pt-BR')}`,
    dados.alcalinidade_mgl === null
      ? null
      : `alcalinidade ${dados.alcalinidade_mgl.toLocaleString('pt-BR')} mg/L`,
  ].filter((x) => x !== null)
  const item = enfileirar(
    'leitura',
    { ...dados },
    {
      dia: dados.data,
      tanque_id: dados.tanque_id,
      rotulo: onde.rotulo ?? null,
      resumo: partes.join(' · '),
    },
  )
  // A tela precisa da leitura de volta para mostrar o que acabou de ser
  // anotado. Ela ainda não existe no banco, mas existe no aparelho — e para
  // quem está na beira do tanque isso é a mesma coisa.
  return {
    data: dados.data,
    hora: dados.hora,
    temperatura_c: dados.temperatura_c,
    oxigenio_mgl: dados.oxigenio_mgl,
    ceu: dados.ceu,
    pendente: true,
    id: item.id,
  }
}

// ---------------------------------------------------------------------------
// Biometria
// ---------------------------------------------------------------------------

export type MotivoDeBiometria = 'rotina' | 'transferencia' | 'despesca' | 'conferencia'

export interface BiometriaNova {
  ciclo_id: string
  data: string
  /** Informado direto, ou deixado nulo quando vem a amostra. */
  peso_medio_g: number | null
  peixes_amostrados: number | null
  peso_amostra_kg: number | null
  motivo: MotivoDeBiometria
  /**
   * O que a curva do app dizia para este peixe NESTE dia, guardado antes de
   * saber a resposta. Nunca entra em conta nenhuma: existe só para, um dia,
   * medir o erro do próprio estimador.
   */
  peso_estimado_g: number | null
  observacao: string | null
}

export function registrarBiometria(dados: BiometriaNova, onde: Etiqueta = {}): Pendente {
  const medio =
    dados.peixes_amostrados && dados.peso_amostra_kg
      ? (dados.peso_amostra_kg * 1000) / dados.peixes_amostrados
      : dados.peso_medio_g
  return enfileirar(
    'biometria',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: dados.ciclo_id,
      rotulo: onde.rotulo ?? null,
      resumo: medio ? `${Math.round(medio).toLocaleString('pt-BR')} g por peixe` : null,
    },
  )
}

// ---------------------------------------------------------------------------
// A DESPESCA — a ficha inteira sobe de uma vez
// ---------------------------------------------------------------------------
//
// Uma despesca não é um registro: é uma ficha com cabeçalho, N linhas de
// espécie, um encerramento e uma biometria. Sobe INTEIRA ou não sobe — meia
// despesca no banco seria pior que nenhuma, porque o ciclo apareceria
// parcialmente vendido e ninguém saberia o que falta.
//
// Na fila ela ocupa uma vaga só, com um identificador só. O servidor deriva o
// identificador de cada linha a partir dele, de forma determinística, e é isso
// que torna o reenvio inofensivo: a segunda tentativa recalcula exatamente os
// mesmos identificadores e todos esbarram no `ON CONFLICT DO NOTHING`.

export interface LinhaDeDespesca {
  especie: string
  quantidade: number
  peso_total_kg: number
  preco_kg_brl: number | null
}

export interface DespescaNova {
  ciclo_id: string
  data: string
  comprador: string | null
  destino: string | null
  /** Fração, não porcentagem: 3% chega aqui como 0,03. */
  desconto_umidade: number | null
  /** Verdadeiro fecha o ciclo; falso deixa o tanque com peixe. */
  encerra: boolean
  /** Só na parcial: quantos peixes quem despescou estima que ficaram. */
  remanescente: number | null
  /**
   * Quantos peixes o app SUPUNHA na represa neste dia. Vai junto para que o
   * fechamento guarde o que a estimativa dizia no momento em que a balança a
   * desmentiu — sem isso, daqui a um ano não há como medir de quanto era o
   * erro nem para que lado ele pendia.
   */
  saldo_mostrado: number | null
  observacao: string | null
  linhas: LinhaDeDespesca[]
  /** Só para o romaneio reimpresso da fila; o servidor não lê. */
  ciclo_codigo?: string
}

/** Uma despesca gravada, com o que o romaneio precisa para ser reaberto. */
export interface RomaneioEmitido {
  /**
   * Quem é esta despesca. Do banco, o grupo de linhas da mesma ficha; da fila,
   * o identificador do registro — «ciclo + dia» juntaria duas parciais do
   * mesmo dia num papel só.
   */
  chave: string
  ciclo_id: string
  /** Vazio quando a despesca ainda está na fila: o código mora no servidor. */
  ciclo: string
  tanque: string
  data: string
  encerrou: boolean
  comprador: string | null
  destino: string | null
  /** Fração, como no banco: 3% é 0,03. */
  desconto_umidade: number | null
  observacao: string | null
  linhas: LinhaGravada[]
  /** Ainda na fila deste aparelho — o servidor não sabe dela. */
  pendente?: boolean
  /** O servidor recusou e ela ficou na fila: o papel existe, a venda não. */
  recusa?: string
}

const textoOuNulo = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null

const numeroOuNuloDe = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function linhasGravadas(bruto: unknown): LinhaGravada[] {
  return lista(bruto as Record<string, unknown>[] | undefined).map((l) => ({
    especie: textoOuNulo(l['especie']),
    quantidade: Number(l['quantidade'] ?? 0),
    // `numeric` chega como texto do driver; `double precision`, como número.
    peso_total_kg: Number(l['peso_total_kg'] ?? 0),
    preco_kg_brl: numeroOuNuloDe(l['preco_kg_brl']),
  }))
}

export async function listarRomaneios(): Promise<RomaneioEmitido[]> {
  const { romaneios } = await chamar<{ romaneios: Record<string, unknown>[] }>('romaneios')
  return lista(romaneios).map((r) => ({
    chave: `${String(r['ciclo_id'] ?? '')}:${String(r['registrado_em'] ?? '')}:${String(r['encerrou'])}`,
    ciclo_id: String(r['ciclo_id'] ?? ''),
    ciclo: String(r['ciclo'] ?? ''),
    tanque: String(r['tanque'] ?? ''),
    data: String(r['data'] ?? '').slice(0, 10),
    encerrou: r['encerrou'] === true,
    comprador: textoOuNulo(r['comprador']),
    destino: textoOuNulo(r['destino']),
    desconto_umidade: numeroOuNuloDe(r['desconto_umidade']),
    observacao: textoOuNulo(r['observacao']),
    linhas: linhasGravadas(r['linhas']),
  }))
}

/**
 * As despescas ainda na fila deste aparelho, com a mesma cara das do banco.
 * Sem isto, a despesca gravada sem sinal não teria romaneio até sincronizar —
 * e o comprador está esperando na beira da represa.
 */
export function romaneiosNaFila(): RomaneioEmitido[] {
  return pendentes()
    .filter((p) => p.tipo === 'despesca')
    .map((p) => ({
      chave: `fila:${p.id}`,
      ciclo_id: String(p.corpo['ciclo_id'] ?? ''),
      // O código do ciclo viaja no corpo só para isto: o papel reimpresso da
      // fila tem de dizer o mesmo ciclo que o original. O servidor o ignora.
      ciclo: textoOuNulo(p.corpo['ciclo_codigo']) ?? '',
      tanque: p.rotulo ?? '',
      data: p.dia,
      encerrou: p.corpo['encerra'] === true,
      comprador: textoOuNulo(p.corpo['comprador']),
      destino: textoOuNulo(p.corpo['destino']),
      desconto_umidade: numeroOuNuloDe(p.corpo['desconto_umidade']),
      observacao: textoOuNulo(p.corpo['observacao']),
      linhas: linhasGravadas(p.corpo['linhas']),
      pendente: true,
      ...(p.recusa ? { recusa: p.recusa } : {}),
    }))
}

/** Os lançamentos crus de um lote, para a conta de `dominio/fechamento.ts`. */
export interface FechamentoBruto {
  ciclo: {
    id: string
    codigo: string
    fase: Fase
    data_inicio: string
    tanque: string
    area_m2: number
    carga_final_alvo_t_ha: number | null
  }
  encerramento: {
    data: string
    saldo_residual_mostrado: number | null
    observacao: string | null
    quem: string | null
  } | null
  movimentos: (MovimentoDoLote & {
    remessa: string | null
    contraparte: string | null
    comprador: string | null
    destino: string | null
    observacao: string | null
  })[]
  racao: {
    granulometria_min_mm: number | null
    granulometria_max_mm: number | null
    proteina_pct: number | null
    kg: number
    dias: number
  }[]
  faltas: { classificacao: string; dias: number }[]
  dias_com_falta: number
  oxigenio: { data: string; oxigenio_mgl: number }[]
  tratos: { dias: number; kg: number }
  /** O servidor decide: o tratador não recebe preço nem valor. */
  ve_valores: boolean
}

export async function verFechamento(cicloId: string): Promise<FechamentoBruto> {
  const r = await chamar<Record<string, unknown>>(`fechamento?ciclo=${encodeURIComponent(cicloId)}`)
  const c = (r['ciclo'] ?? {}) as Record<string, unknown>
  const e = r['encerramento'] as Record<string, unknown> | null
  const faltas = lista(r['faltas'] as Record<string, unknown>[] | undefined)
  const tratos = (r['tratos'] ?? {}) as Record<string, unknown>
  return {
    ciclo: {
      id: String(c['id'] ?? ''),
      codigo: String(c['codigo'] ?? ''),
      fase: c['fase'] as Fase,
      data_inicio: String(c['data_inicio'] ?? '').slice(0, 10),
      tanque: String(c['tanque'] ?? ''),
      area_m2: Number(c['area_m2'] ?? 0),
      carga_final_alvo_t_ha: numeroOuNuloDe(c['carga_final_alvo_t_ha']),
    },
    encerramento: e
      ? {
          data: String(e['data'] ?? '').slice(0, 10),
          saldo_residual_mostrado: numeroOuNuloDe(e['saldo_residual_mostrado']),
          observacao: textoOuNulo(e['observacao']),
          quem: textoOuNulo(e['quem']),
        }
      : null,
    movimentos: lista(r['movimentos'] as Record<string, unknown>[] | undefined).map((m) => ({
      tipo: String(m['tipo'] ?? ''),
      data: String(m['data'] ?? '').slice(0, 10),
      quantidade: numeroOuNuloDe(m['quantidade']),
      peso_total_kg: numeroOuNuloDe(m['peso_total_kg']),
      peso_medio_g: Number(m['peso_medio_g'] ?? 0),
      especie: textoOuNulo(m['especie']),
      preco_kg_brl: numeroOuNuloDe(m['preco_kg_brl']),
      desconto_umidade: numeroOuNuloDe(m['desconto_umidade']),
      registrado_em: String(m['registrado_em'] ?? ''),
      remessa_chegada: textoOuNulo(m['remessa_chegada'])?.slice(0, 10) ?? null,
      remessa: textoOuNulo(m['remessa']),
      contraparte: textoOuNulo(m['contraparte']),
      comprador: textoOuNulo(m['comprador']),
      destino: textoOuNulo(m['destino']),
      observacao: textoOuNulo(m['observacao']),
    })),
    racao: lista(r['racao'] as Record<string, unknown>[] | undefined).map((x) => ({
      granulometria_min_mm: numeroOuNuloDe(x['granulometria_min_mm']),
      granulometria_max_mm: numeroOuNuloDe(x['granulometria_max_mm']),
      proteina_pct: numeroOuNuloDe(x['proteina_pct']),
      kg: Number(x['kg'] ?? 0),
      dias: Number(x['dias'] ?? 0),
    })),
    faltas: faltas.map((f) => ({ classificacao: String(f['classificacao'] ?? ''), dias: Number(f['dias'] ?? 0) })),
    dias_com_falta: Number(faltas[0]?.['total'] ?? 0),
    oxigenio: lista(r['oxigenio'] as Record<string, unknown>[] | undefined).map((o) => ({
      data: String(o['data'] ?? '').slice(0, 10),
      oxigenio_mgl: Number(o['oxigenio_mgl'] ?? 0),
    })),
    tratos: { dias: Number(tratos['dias'] ?? 0), kg: Number(tratos['kg'] ?? 0) },
    ve_valores: r['ve_valores'] === true,
  }
}

export function registrarDespesca(dados: DespescaNova, onde: Etiqueta = {}): Pendente {
  const peixes = dados.linhas.reduce((t, l) => t + l.quantidade, 0)
  const kg = dados.linhas.reduce((t, l) => t + l.peso_total_kg, 0)
  return enfileirar(
    'despesca',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: dados.ciclo_id,
      rotulo: onde.rotulo ?? null,
      resumo:
        `${peixes.toLocaleString('pt-BR')} peixes · ` +
        `${kg.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kg` +
        (dados.encerra ? ' · encerra o ciclo' : ''),
    },
  )
}

// ---------------------------------------------------------------------------
// A MOVIMENTAÇÃO — transferência e repicagem, a mesma ficha
// ---------------------------------------------------------------------------
//
// Uma ponta pesada, outra contada, e a diferença fica. Como a despesca, sobe
// inteira ou não sobe: meia transferência deixaria peixe saído de uma represa
// sem ter entrado em nenhuma.

export interface DestinoDeMovimentacao {
  tanque_id: string
  /** Nulo quando a represa está vazia: o ciclo nasce nesta operação. */
  ciclo_id: string | null
  quantidade: number
  fase: Fase
}

export interface MovimentacaoNova {
  origem_ciclo_id: string
  data: string
  /** O que a balança da origem marcou — a medida da saída. */
  saida_kg: number
  /** A média da amostra pesada na origem. É ela que converte quilo em peixe. */
  peso_medio_g: number
  encerra: boolean
  remanescente: number | null
  saldo_mostrado: number | null
  observacao: string | null
  destinos: DestinoDeMovimentacao[]
}

export function registrarMovimentacao(
  dados: MovimentacaoNova,
  onde: Etiqueta = {},
): Pendente {
  const peixes = dados.destinos.reduce((t, d) => t + d.quantidade, 0)
  return enfileirar(
    'movimentacao',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: dados.origem_ciclo_id,
      rotulo: onde.rotulo ?? null,
      resumo:
        `${dados.saida_kg.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kg · ` +
        `${peixes.toLocaleString('pt-BR')} peixes em ${dados.destinos.length} represa` +
        (dados.destinos.length > 1 ? 's' : '') +
        (dados.encerra ? ' · encerra o ciclo' : ''),
    },
  )
}

// ---------------------------------------------------------------------------
// A RECEPÇÃO — a chegada do alevino, e o que se viu dela
// ---------------------------------------------------------------------------
//
// Ela abre remessa, ciclo, povoamento, biometria e conformidade. Cinco
// tabelas, uma vaga na fila: ou entra tudo, ou não entra nada.

export interface ConformidadeDoLote {
  uniformidade: 'bom' | 'medio' | 'ruim' | null
  peixes_ativos: boolean | null
  transporte: 'bom' | 'medio' | 'ruim' | null
  aclimatacao_ok: boolean | null
  alcalinidade_mgl: number | null
  ph: number | null
  oxigenio_mgl: number | null
  vazio_sanitario: boolean | null
}

export interface RecepcaoNova {
  tanque_id: string
  data: string
  fase: Fase
  fornecedor: string | null
  documento: string | null
  especie: string | null
  /** O da nota fiscal. */
  quantidade_comprada: number | null
  /** O que chegou de verdade — é ele que entra no ciclo. */
  quantidade_recebida: number
  peso_medio_g: number
  observacao: string | null
  conformidade: ConformidadeDoLote
}

export function registrarRecepcao(dados: RecepcaoNova, onde: Etiqueta = {}): Pendente {
  return enfileirar(
    'recepcao',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: null,
      tanque_id: dados.tanque_id,
      rotulo: onde.rotulo ?? null,
      resumo:
        `${dados.quantidade_recebida.toLocaleString('pt-BR')} alevinos de ` +
        `${Math.round(dados.peso_medio_g).toLocaleString('pt-BR')} g`,
    },
  )
}

// ---------------------------------------------------------------------------
// A APLICAÇÃO — corretivo de água e tratamento sanitário
// ---------------------------------------------------------------------------

export type TipoDeAplicacao = 'corretivo' | 'sanitario'
export type UnidadeDeDose = 'kg' | 'g' | 'L' | 'mL'

export interface Aplicacao {
  id: string
  data: string
  tipo: TipoDeAplicacao
  produto: string
  dose: number
  unidade: UnidadeDeDose
  observacao: string | null
  tanque: string
  /** Nulo quando a represa estava vazia — preparo entre ciclos. */
  ciclo: string | null
}

export interface AplicacaoNova {
  tanque_id: string
  data: string
  tipo: TipoDeAplicacao
  produto: string
  dose: number
  unidade: UnidadeDeDose
  observacao: string | null
}

export async function listarAplicacoes(): Promise<Aplicacao[]> {
  const { aplicacoes } = await chamar<{ aplicacoes: Aplicacao[] }>('aplicacoes')
  return lista(aplicacoes)
}

export function registrarAplicacao(dados: AplicacaoNova, onde: Etiqueta = {}): Pendente {
  return enfileirar(
    'aplicacao',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: null,
      tanque_id: dados.tanque_id,
      rotulo: onde.rotulo ?? null,
      resumo: `${dados.produto} · ${dados.dose.toLocaleString('pt-BR')} ${dados.unidade}`,
    },
  )
}

// ---------------------------------------------------------------------------
// A REMESSA DE PEIXE — uma viagem do trator
// ---------------------------------------------------------------------------
//
// «O operador bloqueia a tela do telefone, guarda o telefone no bolso, volta
// ao alevinário para buscar mais… ficamos expostos durante tempos longos.»
//
// Cada viagem sobe sozinha. A ficha inteira deixou de ficar aberta por horas.

export interface RemessaNova {
  /** A saída inteira, daquela represa, naquele dia. Dele deriva a biometria. */
  operacao_id: string
  /** Um destino planejado. Dele deriva o ciclo que nasce na represa destino. */
  plano_id: string
  origem_ciclo_id: string
  destino_tanque_id: string
  destino_fase: Fase
  data: string
  /** Peixes contados na saída do cesto, nesta viagem. */
  quantidade: number
  /** O peso médio medido na origem, uma vez, e que vale para a operação toda. */
  peso_medio_g: number
  observacao?: string | null
}

export function registrarRemessa(dados: RemessaNova, onde: Etiqueta = {}): Pendente {
  return enfileirar(
    'remessa',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: dados.origem_ciclo_id,
      tanque_id: dados.destino_tanque_id,
      rotulo: onde.rotulo ?? null,
      resumo: `${dados.quantidade.toLocaleString('pt-BR')} peixes`,
    },
  )
}

/**
 * Desfazer uma viagem do trator. As duas linhas — saída e entrada — caem juntas.
 *
 * MANDA O QUE GRAVOU, e não o que foi gravado: a operação e a viagem. É delas
 * que o servidor deriva os dois identificadores, do mesmo jeito que derivou na
 * hora de gravar. A tela não conhece — nem precisa conhecer — os ids das linhas
 * de peixe.
 */
export function anularRemessa(
  operacaoId: string,
  remessaId: string,
  dia: string,
  onde: Etiqueta = {},
): Pendente {
  return enfileirar(
    'anular_remessa',
    { operacao_id: operacaoId, remessa_id: remessaId },
    { dia, rotulo: onde.rotulo ?? null, resumo: 'desfazer a remessa' },
  )
}

/**
 * Passar a régua num ciclo: o tanque zera e para de mostrar saldo que não há.
 *
 * `saldo_residual_mostrado` é o que a TELA dizia na hora, e não o que o banco
 * recalcularia depois — é o que permite, um ano adiante, saber se a pessoa
 * encerrou vendo um número que hoje seria outro.
 */
export function encerrarCiclo(
  dados: { ciclo_id: string; data: string; saldo_residual_mostrado: number | null;
           observacao?: string | null },
  onde: Etiqueta = {},
): Pendente {
  return enfileirar(
    'encerrar_ciclo',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: dados.ciclo_id,
      rotulo: onde.rotulo ?? null,
      resumo: 'passar a régua',
    },
  )
}

// ---------------------------------------------------------------------------
// O ESTOQUE DE RAÇÃO — o galpão
// ---------------------------------------------------------------------------
//
// O TRATO BAIXA O ESTOQUE — decisão do gestor em 22/09/2026. A saída manual
// deixou de existir: ela era o consumo digitado à mão, e o trato já o carrega.
// Dois registros do mesmo fato físico não dão duas medições; dão uma medição e
// uma transcrição, e a transcrição erra.
//
// Sobram dois lançamentos, e nenhum deles é diário: a COMPRA e a PERDA.

export type { Calibragem, Pilha } from '../dominio/pilhas'

/**
 * QUEM VENDE PARA A FAZENDA, numa lista fechada.
 *
 * «Quando eu for comparar ração entre diferentes fornecedores, os cadastros
 * precisam estar corretos» — o gestor. Campo livre aceita o mesmo nome escrito
 * de quatro jeitos, e nenhum relatório se recupera disso.
 *
 * O fornecedor NASCE DO USO, como a ração: não há tela de cadastro, o nome
 * novo viaja junto com a compra e o servidor resolve. Sem isso, cadastrar
 * exigiria sinal justamente no pátio, que é onde não há.
 */
export interface Fornecedor {
  id: string
  nome: string
  ativo: boolean
}

export async function listarFornecedores(): Promise<Fornecedor[]> {
  const { fornecedores } = await chamar<{ fornecedores: Fornecedor[] }>('fornecedores')
  return lista(fornecedores)
}

export type TipoDeMovimento = 'entrada' | 'perda'

export interface RacaoNoGalpao {
  racao_id: string
  granulometria_min_mm: number | null
  granulometria_max_mm: number | null
  proteina_pct: number | null
  peso_saco_kg: number
  ativa: boolean
  /** Compras + ajustes − perdas − saídas legadas − tratos válidos. */
  saldo_kg: number
  /** Tudo que já foi comprado desta ração, para separar os dois negativos. */
  entrou_kg: number
  /** Quilos que os tratos consumiram na janela curta. Leitura, não saldo. */
  consumido_kg: number
  /** Nulo quando nunca se lançou uma compra desta ração. */
  ultima_entrada: string | null
  /**
   * As pilhas desta ração, da mais velha para a mais nova — o FIFO já
   * resolvido pelo servidor, com a mesma função que o teste defende.
   */
  pilhas: Pilha[]
  /** Consumo que não achou pilha: falta lançar compra, ou a ração está errada. */
  consumo_sem_pilha_kg: number
  /** O ritmo DESTA ração, não o do galpão. A pilha de alevinagem dura mais. */
  ritmo_kg_dia: number
  /** Por volta de quando a pilha aberta acaba. Estimativa. */
  acaba_em: string | null
  calibragem: Calibragem
}

export interface MovimentoDeRacao {
  id: string
  data: string
  /**
   * O que o LIVRO-RAZÃO pode conter, que é mais do que o app pode escrever.
   * `saida` e `ajuste` não se gravam mais, e continuam existindo em linhas
   * antigas — regra 5, o que foi gravado fica.
   */
  tipo: 'entrada' | 'saida' | 'perda' | 'ajuste'
  unidade_informada: 'saco' | 'kg'
  quantidade_informada: number
  quantidade_kg: number
  peso_saco_kg: number | null
  fabricante: string | null
  nome_comercial: string | null
  fornecedor: string | null
  documento: string | null
  custo_total_brl: string | number | null
  observacao: string | null
  granulometria_min_mm: number | null
  granulometria_max_mm: number | null
  proteina_pct: number | null
  ciclo: string | null
  /** O preço desta compra, vindo da nota válida que a cobre. Só no escritório. */
  preco?: PrecoDaCompra | null
  /** Tem nota? Para todos, sem o valor — esconde o «desfazer» que o servidor recusaria. */
  tem_preco?: boolean
}

/** O preço de uma compra, já com desconto e frete da nota divididos. */
export interface PrecoDaCompra {
  custo_brl: number
  preco_kg_brl: number
  /** Nulo quando a compra foi lançada em quilo. */
  preco_saco_brl: number | null
  nota_id: string
  documento: string | null
  lancada_em: string
  lancada_por: string | null
}

/** Uma compra como ela aparece dentro de uma nota — e no formulário dela. */
export interface CompraDaNota {
  mov_racao_id: string
  valor_brl: number
  data: string
  quantidade_kg: number
  unidade_informada: 'saco' | 'kg'
  quantidade_informada: number
  peso_saco_kg: number | null
  documento: string | null
  fornecedor: string | null
  granulometria_min_mm: number | null
  granulometria_max_mm: number | null
  proteina_pct: number | null
}

export interface NotaDeRacao {
  id: string
  documento: string | null
  unidade: 'saco' | 'kg'
  desconto_brl: number
  frete_brl: number
  observacao: string | null
  registrado_em: string
  registrado_por: string | null
  itens: CompraDaNota[]
}

/** Uma compra lançada no barracão que ainda espera o preço da nota. */
export interface CompraSemPreco {
  id: string
  data: string
  unidade_informada: 'saco' | 'kg'
  quantidade_informada: number
  quantidade_kg: number
  peso_saco_kg: number | null
  documento: string | null
  fornecedor: string | null
  granulometria_min_mm: number | null
  granulometria_max_mm: number | null
  proteina_pct: number | null
}

export interface Galpao {
  racoes: RacaoNoGalpao[]
  movimentos: MovimentoDeRacao[]
  /** Só para gestor e ADM — vazios para o tratador. */
  notas: NotaDeRacao[]
  sem_preco: CompraSemPreco[]
  /** O peso do saco da fazenda — a régua que converte TODO o consumo. */
  peso_saco_kg: number
  /** O dia do SERVIDOR. O relógio do aparelho erra, e a pilha não pode errar junto. */
  hoje: string
  dias_consumo: number
  dias_ritmo: number
}

export interface MovimentoNovo {
  data: string
  tipo: TipoDeMovimento
  granulometria_min_mm: number
  granulometria_max_mm: number
  proteina_pct: number
  unidade_informada: 'saco' | 'kg'
  quantidade_informada: number
  /** Só na compra: o peso do saco DAQUELA compra. 25 e 30 convivem. */
  peso_saco_kg?: number
  fabricante?: string | null
  nome_comercial?: string | null
  /** O NOME, e não o identificador: é o servidor que acha ou cria a linha. */
  fornecedor?: string | null
  documento?: string | null
  custo_total_brl?: number | null
  /** Só na perda, e opcional: «molhou a ração que estava naquele tanque». */
  ciclo_id?: string | null
  observacao?: string | null
}

export async function verGalpao(): Promise<Galpao> {
  const dados = await chamar<Galpao>('estoque')
  return {
    // `pilhas` faltando vira lista vazia aqui, num lugar só: uma versão do
    // servidor mais velha que esta tela devolveria `undefined.map` na cara do
    // peixeiro, com a tela inteira em branco.
    racoes: lista(dados.racoes).map((r) => ({ ...r, pilhas: lista(r.pilhas) })),
    movimentos: lista(dados.movimentos),
    notas: lista(dados.notas).map((n) => ({ ...n, itens: lista(n.itens) })),
    sem_preco: lista(dados.sem_preco),
    peso_saco_kg: Number(dados.peso_saco_kg) || 25,
    hoje: typeof dados.hoje === 'string' ? dados.hoje : new Date().toISOString().slice(0, 10),
    dias_consumo: Number(dados.dias_consumo) || 30,
    dias_ritmo: Number(dados.dias_ritmo) || 7,
  }
}

/**
 * A PILHA ACABOU — ou sobrou o que se está dizendo.
 *
 * O toque mais barato deste app, e o que produz o número mais caro. Grava duas
 * linhas no servidor (a conferência e o ajuste do saldo), e as duas sobem
 * juntas ou nenhuma sobe — regra 13, com identificador derivado deste `id`.
 */
export function fecharPilha(
  dados: { remessa_id: string; data: string; sobrou_kg: number; observacao?: string | null },
  onde: Etiqueta = {},
): Pendente {
  return enfileirar(
    'fechar_pilha',
    { ...dados },
    {
      dia: dados.data,
      rotulo: onde.rotulo ?? null,
      resumo:
        dados.sobrou_kg === 0
          ? 'a pilha acabou'
          : `sobraram ${dados.sobrou_kg.toLocaleString('pt-BR')} kg`,
    },
  )
}

/**
 * DESFAZER UM LANÇAMENTO DE ESTOQUE — a compra que entrou errada.
 *
 * Vai pela fila como tudo o mais: quem lança a nota pode estar sem sinal, e
 * descobrir o erro cinco minutos depois não pode depender de internet.
 */
export function anularMovimentoDeRacao(
  movimentoId: string,
  dia: string,
  onde: Etiqueta = {},
): Pendente {
  return enfileirar(
    'anular_mov_racao',
    { movimento_id: movimentoId },
    { dia, rotulo: onde.rotulo ?? null, resumo: 'desfazer o lançamento' },
  )
}

/**
 * OCULTAR UMA RAÇÃO DO GALPÃO, ou trazê-la de volta.
 *
 * NÃO vai pela fila, e é a exceção: isto é cadastro, não lançamento. A ordem
 * importa — ocultar antes de o «desfazer» da compra ter subido faria o
 * servidor recusar por causa do saldo que ele ainda enxerga. Sendo chamada
 * direta, ela só acontece quando o servidor já concorda, e a recusa vira uma
 * frase na tela em vez de um registro recusado dormindo na fila.
 */
export async function ocultarRacao(racaoId: string, ativa: boolean): Promise<void> {
  await chamar<{ racao: unknown }>('ocultar-racao', { racao_id: racaoId, ativa })
}

export interface NotaNova {
  documento: string | null
  unidade: 'saco' | 'kg'
  desconto_brl: number
  frete_brl: number
  observacao: string | null
  itens: { mov_racao_id: string; valor_brl: number }[]
  /** Corrigir ou desfazer: a nota que esta substitui. Sem itens, só desfaz. */
  anula_nota_id?: string | null
}

/**
 * O PREÇO DA NOTA — lançar, corrigir ou desfazer, numa ficha só.
 *
 * Vai pela fila como tudo o mais. O dia da fila é o de hoje: a nota não tem
 * data de campo, e o que ela muda é o preço de compras de qualquer dia.
 */
export function lancarNotaDeRacao(dados: NotaNova, resumo: string): Pendente {
  return enfileirar(
    'nota_racao',
    { ...dados },
    {
      dia: new Date().toISOString().slice(0, 10),
      rotulo: dados.documento ? `nota ${dados.documento}` : 'nota de ração',
      resumo,
    },
  )
}

/** Um aviso do escritório. A lista vai crescer — por isso tem `tipo`. */
export interface Alerta {
  tipo: 'compras_sem_preco'
  /** Uma a uma, para o aparelho descontar as que já têm preço na fila. */
  compras: { id: string; data: string }[]
}

/**
 * O QUE O ESCRITÓRIO JÁ FEZ E AINDA NÃO SUBIU — as duas memórias.
 *
 * As compras cujo preço está numa nota na fila, e as notas que uma correção ou
 * desfazer na fila vai substituir. Sem somar isto, a tela mostra o preço antigo
 * depois de alguém o ter corrigido, e a pessoa corrige de novo.
 */
export function precosNaFila(): { compras: Set<string>; notasSubstituidas: Set<string> } {
  const compras = new Set<string>()
  const notasSubstituidas = new Set<string>()
  for (const p of pendentes()) {
    if (p.tipo !== 'nota_racao') continue
    const anula = p.corpo['anula_nota_id']
    if (typeof anula === 'string') notasSubstituidas.add(anula)
    const itens = Array.isArray(p.corpo['itens']) ? (p.corpo['itens'] as unknown[]) : []
    for (const i of itens) {
      const id = (i as Record<string, unknown>)['mov_racao_id']
      if (typeof id === 'string') compras.add(id)
    }
  }
  return { compras, notasSubstituidas }
}

/** Os avisos para gestor e ADM. Sem sinal, nenhum — o app abre igual. */
export async function listarAlertas(): Promise<Alerta[]> {
  const { alertas } = await chamar<{ alertas: Alerta[] }>('alertas')
  return lista(alertas)
}

/** Fechei a pilha errada. Desfaz a conferência e o ajuste juntos. */
export function anularFechamento(contagemId: string, dia: string, onde: Etiqueta = {}): Pendente {
  return enfileirar(
    'anular_fechamento',
    { contagem_id: contagemId },
    { dia, rotulo: onde.rotulo ?? null, resumo: 'desfazer o fechamento' },
  )
}

export function registrarMovimentoDeRacao(dados: MovimentoNovo, onde: Etiqueta = {}): Pendente {
  const quanto =
    dados.unidade_informada === 'saco'
      ? `${dados.quantidade_informada.toLocaleString('pt-BR')} saco(s)`
      : `${dados.quantidade_informada.toLocaleString('pt-BR')} kg`
  const verbo = dados.tipo === 'entrada' ? 'entrou' : 'perdeu'
  return enfileirar(
    'mov_racao',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: dados.ciclo_id ?? null,
      rotulo: onde.rotulo ?? null,
      resumo: `${verbo} ${quanto}`,
    },
  )
}

// ---------------------------------------------------------------------------
// AS PERDAS — e o que este número não é
// ---------------------------------------------------------------------------
//
// «O número de peixes que boiam é inferior ao número de peixes que afundam,
// portanto acaba sendo um reporte não determinístico; o furo aparecerá ao
// final do ciclo.» O que se conta é o que foi ENCONTRADO, e é um piso.

export type TipoDePerda = 'mortalidade' | 'perda_manejo'

export interface PerdaNova {
  ciclo_id: string
  data: string
  tipo: TipoDePerda
  quantidade: number
  observacao: string | null
}

export function registrarPerda(dados: PerdaNova, onde: Etiqueta = {}): Pendente {
  return enfileirar(
    'perda',
    { ...dados },
    {
      dia: dados.data,
      ciclo_id: dados.ciclo_id,
      rotulo: onde.rotulo ?? null,
      resumo:
        `${dados.quantidade.toLocaleString('pt-BR')} recolhidos · ` +
        (dados.tipo === 'perda_manejo' ? 'pós-manejo' : 'morte comum'),
    },
  )
}

/**
 * ANULAR UM TRATO JÁ SINCRONIZADO.
 *
 * Nada se apaga: isto enfileira um pedido que faz o servidor gravar um trato
 * novo apontando para o antigo. Os dois ficam no histórico, e a conta passa a
 * ignorar os dois.
 *
 * Vai pela fila como tudo o mais — anular sem sinal precisa funcionar, e o
 * identificador de aparelho torna o reenvio inofensivo aqui também.
 */
export function anularTrato(tratoId: string, onde: Etiqueta & { dia: string }): Pendente {
  return enfileirar(
    'anular_trato',
    { trato_id: tratoId },
    {
      dia: onde.dia,
      ciclo_id: null,
      rotulo: onde.rotulo ?? null,
      resumo: 'anular um trato já enviado',
    },
  )
}

// ---------------------------------------------------------------------------
// Os parâmetros da conta — o servidor manda, o aparelho calcula
// ---------------------------------------------------------------------------

/** Uma linha da tabela `parametros`: o que a fazenda configura sem tocar no código. */
export interface ParametroDoCalculo {
  chave: string
  valor: string
}

export interface FaixaDeArracoamento {
  peso_de_g: number
  peso_ate_g: number
  racao_indicada: string | null
  ref_dia: number
  pct_pv_min: number
  pct_pv_max: number
  pct_eficiencia: number | null
  ca_esperada: number
  fase: Fase | null
}

export interface TetoAmbiental {
  tanque_id: string | null
  mes_inicio: number
  mes_fim: number
  kg_ha_dia: number
  com_aeracao: boolean
  origem: string
}

export interface FaixaDeCoeficiente {
  fator: 'oxigenio_mgl' | 'temperatura_c'
  de: number
  ate: number | null
  coeficiente: number
  acao: 'reduzir' | 'adiar'
}

export interface ParametrosDoCalculo {
  parametros: ParametroDoCalculo[]
  arracoamento: FaixaDeArracoamento[]
  tetos: TetoAmbiental[]
  coeficientes: FaixaDeCoeficiente[]
}

export async function parametrosDoCalculo(): Promise<ParametrosDoCalculo> {
  const c = await chamar<Partial<ParametrosDoCalculo>>('calculo')
  // As quatro listas alimentam a conta do trato. Qualquer uma faltando
  // derrubaria a tela; vazia ela apenas faz o app dizer o que falta.
  return {
    parametros: lista(c.parametros),
    arracoamento: lista(c.arracoamento),
    tetos: lista(c.tetos),
    coeficientes: lista(c.coeficientes),
  }
}

/** Lê um parâmetro numérico pela chave, com padrão se ele ainda não existir. */
export function numeroDoParametro(
  lista: { chave: string; valor: string }[],
  chave: string,
  padrao: number,
): number {
  const achado = lista.find((p) => p.chave === chave)
  const n = achado ? Number(achado.valor) : NaN
  return Number.isFinite(n) ? n : padrao
}

/** O mesmo, para parâmetro que é texto — lista, faixa, receita. */
export function textoDoParametro(
  lista: { chave: string; valor: string }[],
  chave: string,
): string | null {
  return lista.find((p) => p.chave === chave)?.valor ?? null
}

// ---------------------------------------------------------------------------
// Aparelhos
// ---------------------------------------------------------------------------

export async function listarAparelhos(): Promise<Aparelho[]> {
  const { sessoes } = await chamar<{ sessoes: Aparelho[] }>('sessoes')
  return sessoes
}

export async function revogarAparelho(sessaoId: string, motivo: string): Promise<void> {
  await chamar('revogar', { sessao_id: sessaoId, motivo })
}
